import {
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
    styled,
} from "@mui/material"
import { ReactElement } from "react"

import StackedKeyValueList from "./stackedKeyValueList"
import { DATA_DISPLAY_FALLBACK } from "../../constants/generalConstants"
import { useScreenSize } from "../../hooks/useScreenSize"
import { displayText } from "../../utils/general/generalUtils"

/**
 * Styled version of MUI `TableCell` with all borders removed
 */
export const BorderlessCell = styled(TableCell)(({ theme }) => ({
    border: 0,
    paddingLeft: 0,
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    minWidth: theme.spacing(30),
}))

export type KeyValueType = {
    [key: string]: string | undefined | number | boolean
}

type VerticalKeyValueListProps = {
    kwargs: KeyValueType
    alwaysUseMobileTypography?: boolean
    overrideColour?: string
}

/**
 * Returns list key value pairs as defined by input prop `kwargs`. If
 * on a mobile screen it defaults to the stacked vertical key value list.
 *
 * Runs each value through `displayText` to ensure it is a string
 * representation of the value and filters out any values that are
 * == `DATA_DISPLAY_FALLBACK`.
 *
 * @example
 * <VerticalKeyValueList kwargs={{
 *         key1: "value1"
 *     }}
 * />
 */
const VerticalKeyValueList = ({
    kwargs,
    alwaysUseMobileTypography = false,
    overrideColour,
}: VerticalKeyValueListProps): ReactElement => {
    const { isMob } = useScreenSize()
    if (isMob) {
        return (
            <StackedKeyValueList
                details={kwargs}
                overrideColour={overrideColour}
            />
        )
    }

    const keyColour = alwaysUseMobileTypography
        ? "text.secondary"
        : "text.disabled"
    const valueColour = alwaysUseMobileTypography ? "text.primary" : undefined

    const valueFontWeight = alwaysUseMobileTypography ? "500" : undefined

    return (
        <Table>
            <TableBody>
                {Object.keys(kwargs)
                    .filter(
                        (key) =>
                            displayText(kwargs[key]) !== DATA_DISPLAY_FALLBACK
                    )
                    .map((key: string) => (
                        <TableRow key={key}>
                            <BorderlessCell>
                                <Typography color={overrideColour ?? keyColour}>
                                    {key}
                                </Typography>
                            </BorderlessCell>
                            <BorderlessCell>
                                <Typography
                                    color={overrideColour ?? valueColour}
                                    fontWeight={valueFontWeight}
                                >
                                    {" "}
                                    {displayText(kwargs[key])}
                                </Typography>
                            </BorderlessCell>
                        </TableRow>
                    ))}
            </TableBody>
        </Table>
    )
}

VerticalKeyValueList.defaultProps = {
    alwaysUseMobileTypography: false,
    overrideColour: undefined,
}

export default VerticalKeyValueList
